import makeStrictKeysObject from "@/util/strict-keys-object.mjs";

type MirroredObject<K extends PrimitiveKey> = {
  [P in K]: P;
};

/**
 * A mirror object is one where all the keys are equivalent to their values.
 * This also means that values are limited to primitives.
 *
 * There are also some additional constraints:
 * - Looking up a key that doesn't exist will result in a TypeError.
 * - The resulting object is frozen.
 *
 * The motivation for this is mainly so we can have convenient syntax
 * without surprises, like changed key-values.
 */
export default function mirrorObject<const K extends PrimitiveKey>(
  keys: K[],
): Readonly<MirroredObject<K>> {
  const result = {} as MirroredObject<K>;

  for (const key of keys) {
    result[key] = key;
  }

  Object.freeze(result);

  return makeStrictKeysObject(result);
}
